import type { ButtonProps } from '@mui/material/Button';
import type { SxProps, Theme } from '@mui/material/styles';
import isEqual from 'lodash/isEqual';
import { type Dispatch, memo, type ReactElement, type SetStateAction, useMemo } from 'react';
import type { ListBulkAddFooterProps } from '../List/components/ListBulkAddFooter/ListBulkAddFooter.tsx';
import type { ListHeaderActionsProps } from '../List/components/ListHeaderActions/ListHeaderActions.tsx';
import type { ListTopBarProps } from '../List/components/ListTopBar/ListTopBar.tsx';
import type { VirtualizedListProps } from '../List/components/ListVirtualized/ListVirtualized.tsx';
import type { ListItemFieldsFilterProps } from '../List/filters/ListItemFieldsFilter/ListItemFieldsFilter.tsx';
import { List } from '../List/List.tsx';
import { TRANSFER_LIST_MIN_SEARCH_PHRASE_LENGTH } from './constants.ts';
import { useTransferListItemSizes } from './hooks/useTransferListItemSizes.ts';
import type { TransferListBaseItem, TransferListItemProps, TransferListItemsMap } from './types.ts';

export type TransferListPanelProps<TItem extends TransferListBaseItem> = {
  ListItemComponent: VirtualizedListProps<TItem, TransferListItemProps<TItem>>['ListItemComponent'];
  dataTest?: string;
  checkboxDataTest?: string;
  emptyListText?: string;
  fields: ListItemFieldsFilterProps<TItem>['fields'];
  hasError?: boolean;
  headerButtonColor: ButtonProps['color'];
  headerButtonText: string;
  isDisabled?: boolean;
  isItemBlocked?: TransferListItemProps<TItem>['isBlocked'];
  isLoading?: boolean;
  isSortedDescending?: boolean;
  items: TransferListItemsMap<TItem>;
  itemsSelected: TransferListItemsMap<TItem>;
  listHeight?: number;
  onSearch?: (item: TItem, searchPhrase: string) => boolean;
  onSelectAll: ListHeaderActionsProps['onCheckboxChange'];
  onSelectItem: TransferListItemProps<TItem>['onSelect'];
  onTransfer: () => void;
  searchBarOptions?: ListTopBarProps['options'];
  searchBarPlaceholder?: ListTopBarProps['searchBarPlaceholder'];
  searchInputDataTest?: string;
  searchPhrase: string;
  setSearchPhrase: Dispatch<SetStateAction<string>>;
  windowWidth: number;
} & TransferListPanelPropsOptionalFooterProps<TItem>;

type TransferListPanelPropsOptionalFooterProps<TItem extends TransferListBaseItem> =
  | TransferListPanelPropsWithFooterProps<TItem>
  | TransferListPanelPropsWithoutFooterProps;

type TransferListPanelPropsWithFooterProps<TItem extends TransferListBaseItem> = {
  onBulkAdd: ListBulkAddFooterProps<TItem>['onBulkAdd'];
  onBulkValidate: ListBulkAddFooterProps<TItem>['onValidate'];
};

type TransferListPanelPropsWithoutFooterProps = {
  onBulkAdd?: undefined;
  onBulkValidate?: undefined;
};

export const TransferListPanel = memo(
  <TItem extends TransferListBaseItem>({
    ListItemComponent,
    dataTest,
    checkboxDataTest,
    emptyListText,
    fields,
    hasError,
    headerButtonColor,
    headerButtonText,
    isDisabled,
    isItemBlocked,
    isLoading,
    isSortedDescending,
    items,
    itemsSelected,
    listHeight,
    onBulkAdd,
    onBulkValidate,
    onSearch,
    onSelectAll,
    onSelectItem,
    onTransfer,
    searchBarOptions,
    searchBarPlaceholder,
    searchInputDataTest,
    searchPhrase,
    setSearchPhrase,
    windowWidth,
  }: TransferListPanelProps<TItem>) => {
    const errorSx: SxProps<Theme> | undefined = hasError ? { borderColor: 'error.main' } : undefined;
    const data = useMemo<TItem[]>(() => {
      const direction = isSortedDescending ? -1 : 1;

      if (onSearch && searchPhrase.length >= TRANSFER_LIST_MIN_SEARCH_PHRASE_LENGTH) {
        return Object.values(items)
          .filter(item => onSearch(item, searchPhrase))
          .sort((a, b) => (a.name || '').localeCompare(b.name || '') * direction);
      }

      return Object.values(items).sort((a, b) => (a.name || '').localeCompare(b.name || '') * direction);
    }, [isSortedDescending, items, onSearch, searchPhrase]);

    const { getItemSize, setItemSize } = useTransferListItemSizes();

    const expensiveHeaderActionsProps = useMemo<
      Pick<ListHeaderActionsProps, 'buttons' | 'itemsCount' | 'selectedItemsCount'>
    >(
      () => ({
        buttons: [
          {
            color: headerButtonColor,
            onClick: onTransfer,
            text: headerButtonText,
          },
        ],
        itemsCount: Object.keys(items).length,
        selectedItemsCount: Object.keys(itemsSelected).length,
      }),
      [headerButtonColor, headerButtonText, items, itemsSelected, onTransfer]
    );

    return (
      <List.Root dataTest={dataTest} sx={errorSx}>
        <List.TopBar
          isDisabled={isDisabled}
          isLoading={isLoading}
          options={searchBarOptions}
          searchBarPlaceholder={searchBarPlaceholder}
          searchInputDataTest={searchInputDataTest}
          searchPhrase={searchPhrase}
          setSearchPhrase={setSearchPhrase}
        />

        <List.Divider />

        <List.HeaderActions
          {...expensiveHeaderActionsProps}
          checkboxDataTest={checkboxDataTest}
          isDisabled={isDisabled || isLoading}
          onCheckboxChange={onSelectAll}
        />

        <List.Divider sx={errorSx} />

        <List.Content<TItem, TransferListItemProps<TItem>>
          data={data}
          emptyListText={emptyListText}
          height={listHeight}
          isLoading={isLoading}
          itemSize={getItemSize}
          ListItemComponent={ListItemComponent}
          listItemProps={{
            fields,
            isBlocked: isItemBlocked,
            isDisabled,
            itemsSelected,
            onSelect: onSelectItem,
            searchPhrase,
            setItemSize,
            windowWidth,
          }}
        />

        {onBulkAdd && (
          <>
            <List.Divider sx={errorSx} />
            <List.BulkAddFooter<TItem>
              isDisabled={isDisabled || isLoading}
              onBulkAdd={onBulkAdd}
              onValidate={onBulkValidate}
            />
          </>
        )}
      </List.Root>
    );
  },
  (prevProps, nextProps) => {
    const hasSameDisabledState = prevProps.isDisabled === nextProps.isDisabled;
    const hasSameErrorState = prevProps.hasError === nextProps.hasError;
    const hasSameFieldsConfig = isEqual(prevProps.fields, nextProps.fields);
    const hasSameLoadingState = prevProps.isLoading === nextProps.isLoading;
    const hasSameSortedDescendingState = prevProps.isSortedDescending === nextProps.isSortedDescending;
    const hasSameItems = isEqual(prevProps.items, nextProps.items);
    const hasSameItemsSelected = isEqual(prevProps.itemsSelected, nextProps.itemsSelected);
    const hasSameSearchPhrase = prevProps.searchPhrase === nextProps.searchPhrase;
    const hasSameWindowWidth = prevProps.windowWidth === nextProps.windowWidth;

    return (
      hasSameSearchPhrase &&
      hasSameItemsSelected &&
      hasSameItems &&
      hasSameLoadingState &&
      hasSameDisabledState &&
      hasSameErrorState &&
      hasSameSortedDescendingState &&
      hasSameFieldsConfig &&
      hasSameWindowWidth
    );
  }
) as <TItem extends TransferListBaseItem>(props: TransferListPanelProps<TItem>) => ReactElement;
